import React, { useMemo, useState } from 'react';
import { Tooltip, Button, Icon, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { SeparateNumberFormat, calculateValueByCpm } from '../../../../../services/utils';
import AudienceSummaryComponentGeneral from '../../../../shared/components/AudienceSummaryComponentGeneral';
import {
  getAmountAudienceDisplay,
  getAmountAudience,
  getAppliedVolumeDiscount,
  getSpotsPerDay
} from '../../../../../services/display';
import AudienceImpactsHolder from '../../styles/audienceImpactsStyle';
import NumberFormat from '../../../../shared/components/NumberFormat';
import InvestmentManagerComponent from './InvestmentManagerComponent';
import Colors from '../../../../../themes/Colors';

const TotalInvestmentComponent = ({
  currentCampaign,
  displays,
  type = 'default',
  audience,
  relationId,
  editCampaignActive,
  user,
  audienceHoursOfDay,
  audienceDaysOfWeek,
  audienceQualitative,
  getAudienceHoursOfDayRequest,
  getAudienceDaysOfWeekRequest,
  getAudienceQualitativeRequest,
  currency,
  display,
  customContents = [],
  cpmDisplay,
  isProgrammatic,
  durationCampaign,
  parentGroupInvestment,
  handleChangeGroupProposal,
  isPaymentStatusApproved,
  isFixedBudgetDisplay,
  group,
  relation,
  date_from,
  date_to,
  isSecured,
  isSubsidized,
  // Rendered right under the price chip, so the subsidize control sits next to
  // the number it zeroes.
  subsidizeSlot
}) => {
  const [visibleAudienceView, setVisibleAudienceView] = useState(false);
  const { t } = useTranslation();

  const [totalAudience, totalImpacts] = useMemo(() => {
    let totalAudience = 0;
    let totalImpacts = 0;
    const displayList = Array.isArray(displays) ? displays : [displays];
    if (type === 'relation') {
      totalAudience = getAmountAudience(audience, relationId) || 0;
      totalImpacts = getAmountAudience(audience, relationId, true) || 0;
    } else {
      displayList.forEach(display => {
        totalAudience += getAmountAudienceDisplay(audience, display.id) || 0;
        totalImpacts += getAmountAudienceDisplay(audience, display.id, true) || 0;
      });
    }
    return [totalAudience, totalImpacts];
  }, [audience, displays]);

  const totalInvestment = useMemo(() => {
    let totalValue = 0;
    if (isProgrammatic) {
      const relations = (customContents || []).filter(item => item.displayId === display.id);
      return relations.reduce((total, relation) => {
        const spotValue =
          Math.floor(
            parseFloat(
              calculateValueByCpm(cpmDisplay, relation.promised_shows, 'calculate_price')
            ) * 100
          ) / 100 || 0;
        return total + spotValue;
      }, 0);
    } else {
      const pricePerDay = display.price_per_day || 0;
      const daysOfCampaign = durationCampaign || 0;
      const total = pricePerDay * daysOfCampaign;
      totalValue = total;
    }
    return totalValue;
  }, [customContents, cpmDisplay, currentCampaign, durationCampaign]);

  const getTooltipImpacts = () => {
    return t(
      'Real-time screen impressions, shows the number of people who will see your spot or group of spots based on camera measurement data, along with dates, selected rules, and investment made. Remember that the greater the investment, the greater the impact it will generate.'
    );
  };

  const showAudienceModal = () => {
    setVisibleAudienceView(!visibleAudienceView);
  };

  return (
    <AudienceImpactsHolder>
      <div
        className={
          type === 'relation'
            ? 'col-displays content-audience-relation'
            : 'col-displays content-audience'
        }
      >
        <div>
          <InvestmentManagerComponent
            currency={currency}
            value={totalInvestment}
            parentGroupInvestment={parentGroupInvestment}
            onChange={(value, typeOfUpdate) => {
              handleChangeGroupProposal(
                value,
                [display],
                totalInvestment,
                typeOfUpdate,
                parentGroupInvestment,
                group.displays.filter(groupDisplay => groupDisplay.id !== display.id)
              );
            }}
            disabled={isPaymentStatusApproved || isFixedBudgetDisplay || isSecured}
            readOnly={!isProgrammatic}
            step={display.cpmi * (Array.isArray(displays) ? displays.length : 1) + 1}
            tooltip={t(
              'This indicates what percentage of the total budget allocated to the group corresponds to the investment assigned to this screen. You can modify it by editing either the investment or directly the percentage.'
            )}
          />
        </div>
        <div>
          <div
            className="screenCardAudit"
            size="small"
            style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            {(() => {
              // Traditional is priced per day, CPM does not apply.
              const base = isProgrammatic ? display.cpmi : display.price_per_day || 0;
              const label = isProgrammatic ? t('Screen CPM') : t('Cost per day');

              let discountedCpmi = null;
              if (isProgrammatic) {
                // Calculate total spots per day from all relations of this display
                const displayRelations = customContents
                  ? customContents.filter(item => item.displayId === display.id)
                  : [];
                const totalSpotsPerDay = displayRelations.reduce((total, rel) => {
                  return total + getSpotsPerDay(rel, date_from, date_to);
                }, 0);
                // Real campaign total (sum of promised_shows) for "total" discounts
                const totalPromisedShows = displayRelations.reduce(
                  (total, rel) => total + (rel.promised_shows || 0),
                  0
                );
                const discount =
                  relation && date_from && date_to
                    ? getAppliedVolumeDiscount(display, totalSpotsPerDay, totalPromisedShows)
                    : null;
                if (discount && discount.final_cpmi && !isPaymentStatusApproved) {
                  discountedCpmi = discount.final_cpmi;
                }
              }
              // Subsidizing the whole screen drops what it costs to zero, the same
              // way the effective CPM does for a single subsidized ad. It wins over
              // the volume discount. `null` means nothing lowered the price.
              const finalPrice = isSubsidized ? 0 : discountedCpmi;
              // A volume discount reads green and a subsidized screen reads blue;
              // when both apply the discount colour wins.
              const finalColor =
                discountedCpmi !== null ? '#52c41a' : undefined;

              const showsFinal = finalPrice !== null;

              return (
                <>
                  {/* Always mounted so the row can grow into it instead of jumping.
                      The negative margin cancels the flex gap while collapsed. */}
                  <span
                    style={{
                      textDecoration: 'line-through',
                      color: Colors.textMuted,
                      display: 'inline-block',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      maxWidth: showsFinal ? '160px' : 0,
                      opacity: showsFinal ? 1 : 0,
                      marginRight: showsFinal ? 0 : '-4px',
                      transition:
                        'max-width 0.3s ease-in-out, opacity 0.3s ease-in-out, margin 0.3s ease-in-out'
                    }}
                  >
                    <NumberFormat value={base} currency={currency} />
                  </span>
                  <span style={{ fontWeight: 600, color: finalColor }}>
                    <NumberFormat value={showsFinal ? finalPrice : base} currency={currency} />
                  </span>
                  {label}
                </>
              );
            })()}
          </div>
        </div>
        {subsidizeSlot}
        {/* Only the impacts chip depends on audience data; investment, price and
            the subsidize control must show with or without it. */}
        {totalAudience > 0 && (
          <div>
            <Tooltip placement="bottomLeft" title={getTooltipImpacts()}>
              <Button className="screenCardAudit" size="small" onClick={() => showAudienceModal()}>
                <Icon type="team" theme="outlined" />
                <span style={{ fontSize: '12px' }}>{SeparateNumberFormat(totalImpacts)}</span>
              </Button>
            </Tooltip>
          </div>
        )}
        {type === 'display' && (
          <Modal
            title={t('Audience')}
            visible={visibleAudienceView}
            onOk={() => showAudienceModal()}
            onCancel={() => showAudienceModal()}
            width={1000}
          >
            <AudienceSummaryComponentGeneral
              t={t}
              display={displays}
              user={user}
              campaign={editCampaignActive ? currentCampaign : null}
              audienceHoursOfDay={audienceHoursOfDay}
              audienceDaysOfWeek={audienceDaysOfWeek}
              getAudienceHoursOfDayRequest={getAudienceHoursOfDayRequest}
              getAudienceDaysOfWeekRequest={getAudienceDaysOfWeekRequest}
              getAudienceQualitativeRequest={getAudienceQualitativeRequest}
              audienceQualitative={audienceQualitative}
            />
          </Modal>
        )}
      </div>
    </AudienceImpactsHolder>
  );
};

export default TotalInvestmentComponent;
